import React, { useMemo, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
interface AddGLPostingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
}
interface Transaction {
  id: string;
  account: string;
  nature: 'Debit' | 'Credit';
  narration: string;
  amount: number;
}
export function AddGLPostingModal({
  isOpen,
  onClose,
  onSave
}: AddGLPostingModalProps) {
  const [purpose, setPurpose] = useState('Disbursement');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  // Form state for new transaction
  const [account, setAccount] = useState('Cash Account');
  const [nature, setNature] = useState<'Debit' | 'Credit'>('Debit');
  const [narration, setNarration] = useState('');
  const [amount, setAmount] = useState('');
  const today = new Date().toISOString().split('T')[0];
  const voucherNumber = 'VCH-' + today.replace(/-/g, '') + '-' + Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  const handleAddTransaction = () => {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) return;
    const newTx: Transaction = {
      id: Math.random().toString(36).substr(2, 9),
      account,
      nature,
      narration,
      amount: Number(amount)
    };
    setTransactions([...transactions, newTx]);
    setNarration('');
    setAmount('');
  };
  const handleRemoveTransaction = (id: string) => {
    setTransactions(transactions.filter(t => t.id !== id));
  };
  const {
    totalDebit,
    totalCredit
  } = useMemo(() => {
    let debit = 0;
    let credit = 0;
    transactions.forEach(t => {
      if (t.nature === 'Debit') debit += t.amount;else credit += t.amount;
    });
    return {
      totalDebit: debit,
      totalCredit: credit
    };
  }, [transactions]);
  const isBalanced = totalDebit === totalCredit && transactions.length > 0;
  const hasTransactions = transactions.length > 0;
  const handleSave = () => {
    if (!isBalanced) return;
    onSave({
      voucherNumber,
      voucherDate: today,
      postingDate: today,
      purpose,
      transactions,
      totalDebit,
      totalCredit
    });
    // Reset state
    setTransactions([]);
    setPurpose('Disbursement');
    setAccount('Cash Account');
    setNature('Debit');
    setNarration('');
    setAmount('');
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-9">
      <div className="fixed inset-0 bg-gray-900/50 transition-opacity" onClick={onClose} aria-hidden="true" data-id="element-10" />

      <div className="relative w-full max-w-4xl max-h-[90vh] flex flex-col bg-white rounded-lg shadow-xl transition-all" data-id="element-11">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-12">
          <h3 className="text-lg font-semibold leading-6 text-gray-900" data-id="element-13">
            Add GL Posting
          </h3>
          <button type="button" className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none" onClick={onClose} data-id="element-14">
            <span className="sr-only" data-id="element-15">Close</span>
            <X className="h-6 w-6" aria-hidden="true" data-id="element-16" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-17">
          {/* Header Info */}
          <div data-id="element-18">
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2" data-id="element-19">
              Voucher Details
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-20">
              <div data-id="element-21">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-22">
                  Voucher Number
                </label>
                <input type="text" readOnly value={voucherNumber} className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm py-2 px-3 border text-gray-500" data-id="element-23" />
              </div>
              <div data-id="element-24">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-25">
                  Voucher Date
                </label>
                <input type="date" readOnly value={today} className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm py-2 px-3 border text-gray-500" data-id="element-26" />
              </div>
              <div data-id="element-27">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-28">
                  Posting Date
                </label>
                <input type="date" readOnly value={today} className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm py-2 px-3 border text-gray-500" data-id="element-29" />
              </div>
              <div data-id="element-30">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-31">
                  Purpose
                </label>
                <select value={purpose} onChange={e => setPurpose(e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border" data-id="element-32">
                  <option value="Disbursement" data-id="element-33">Disbursement</option>
                  <option value="Repayment" data-id="element-34">Repayment</option>
                  <option value="Fee Collection" data-id="element-35">Fee Collection</option>
                  <option value="Penalty" data-id="element-36">Penalty</option>
                  <option value="Adjustment" data-id="element-37">Adjustment</option>
                </select>
              </div>
            </div>
          </div>

          {/* Transaction Entry */}
          <div data-id="element-38">
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2" data-id="element-39">
              Transaction Details
            </h4>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4" data-id="element-40">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end" data-id="element-41">
                <div className="lg:col-span-2" data-id="element-42">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-43">
                    Account
                  </label>
                  <select value={account} onChange={e => setAccount(e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border bg-white" data-id="element-44">
                    <option value="Cash Account" data-id="element-45">Cash Account</option>
                    <option value="Loan Receivable" data-id="element-46">Loan Receivable</option>
                    <option value="Interest Income" data-id="element-47">Interest Income</option>
                    <option value="Fee Income" data-id="element-48">Fee Income</option>
                    <option value="Penalty Income" data-id="element-49">Penalty Income</option>
                    <option value="Customer Account" data-id="element-50">Customer Account</option>
                    <option value="Bank Account" data-id="element-51">Bank Account</option>
                  </select>
                </div>
                <div data-id="element-52">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-53">
                    Nature
                  </label>
                  <select value={nature} onChange={e => setNature(e.target.value as 'Debit' | 'Credit')} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border bg-white" data-id="element-54">
                    <option value="Debit" data-id="element-55">Debit</option>
                    <option value="Credit" data-id="element-56">Credit</option>
                  </select>
                </div>
                <div className="lg:col-span-2" data-id="element-57">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-58">
                    Amount (PKR)
                  </label>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border bg-white" data-id="element-59" />
                </div>
              </div>
              <div className="flex gap-4 items-end" data-id="element-60">
                <div className="flex-1" data-id="element-61">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-62">
                    Narration
                  </label>
                  <input type="text" value={narration} onChange={e => setNarration(e.target.value)} placeholder="Enter transaction details..." className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border bg-white" data-id="element-63" />
                </div>
                <button type="button" onClick={handleAddTransaction} disabled={!amount || Number(amount) <= 0} className="inline-flex items-center justify-center rounded-md bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 border border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-64">
                  <Plus className="h-4 w-4 mr-1" data-id="element-65" />
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Transactions Table */}
          {transactions.length > 0 && <div data-id="element-66">
              <div className="overflow-x-auto ring-1 ring-gray-200 rounded-lg" data-id="element-67">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-68">
                  <thead className="bg-gray-50" data-id="element-69">
                    <tr data-id="element-70">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10" data-id="element-71">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-72">
                        Account
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-73">
                        Narration
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-74">
                        Debit
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-75">
                        Credit
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-16" data-id="element-76">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-77">
                    {transactions.map((t, idx) => <tr key={t.id} data-id="element-78">
                        <td className="px-4 py-2 text-sm text-gray-500" data-id="element-79">
                          {idx + 1}
                        </td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-80">
                          {t.account}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500" data-id="element-81">
                          {t.narration || '—'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right" data-id="element-82">
                          {t.nature === 'Debit' ? `PKR ${t.amount.toLocaleString()}` : '—'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right" data-id="element-83">
                          {t.nature === 'Credit' ? `PKR ${t.amount.toLocaleString()}` : '—'}
                        </td>
                        <td className="px-4 py-2 text-right" data-id="element-84">
                          <button onClick={() => handleRemoveTransaction(t.id)} className="text-red-500 hover:text-red-700" data-id="element-85">
                            <Trash2 className="h-4 w-4" data-id="element-86" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold" data-id="element-87">
                    <tr data-id="element-88">
                      <td colSpan={3} className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-89">
                        Total
                      </td>
                      <td className={`px-4 py-3 text-sm text-right ${!isBalanced && hasTransactions ? 'text-red-600' : 'text-gray-900'}`} data-id="element-90">
                        PKR {totalDebit.toLocaleString()}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right ${!isBalanced && hasTransactions ? 'text-red-600' : 'text-gray-900'}`} data-id="element-91">
                        PKR {totalCredit.toLocaleString()}
                      </td>
                      <td data-id="element-92"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {!isBalanced && hasTransactions && <p className="mt-2 text-sm text-red-600 font-medium text-right" data-id="element-93">
                  Total Debit and Credit amounts must be equal. Difference: PKR{' '}
                  {Math.abs(totalDebit - totalCredit).toLocaleString()}
                </p>}
            </div>}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex flex-row-reverse gap-3 rounded-b-lg border-t border-gray-200" data-id="element-94">
          <button type="button" disabled={!isBalanced || !hasTransactions} className="inline-flex w-full justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleSave} data-id="element-95">
            Save Posting
          </button>
          <button type="button" className="inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto" onClick={onClose} data-id="element-96">
            Cancel
          </button>
        </div>
      </div>
    </div>;
}